import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, Upload, Trash2, Building2, CheckCircle, XCircle, Loader2, FileKey, ArrowLeft, LogOut, RefreshCw, Play, AlertCircle } from 'lucide-react';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

export default function AdminCertificados() {
  const [certificados, setCertificados] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [syncing, setSyncing] = useState({});
  const [testing, setTesting] = useState({});
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [syncResults, setSyncResults] = useState({});
  const [syncingAll, setSyncingAll] = useState(false);
  const [syncAllResults, setSyncAllResults] = useState(null);
  const navigate = useNavigate();
  const { usuario, logout } = useAuth();

  // Form state
  const [selectedEmpresa, setSelectedEmpresa] = useState('');
  const [password, setPassword] = useState('');
  const [file, setFile] = useState(null);

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const response = await api.get('/certificados');
      if (response.data.success) {
        setCertificados(response.data.certificados);
        setEmpresas(response.data.empresas);
      }
    } catch (err) {
      setError('Error cargando datos: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(p12|pfx)$/)) {
        setError('Solo se permiten archivos .p12 o .pfx');
        return;
      }
      setFile(selectedFile);
      setError('');
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!selectedEmpresa || !password || !file) {
      setError('Todos los campos son obligatorios');
      return;
    }

    try {
      setUploading(true);
      setError('');
      setSuccess('');

      const formData = new FormData();
      formData.append('certificado', file);
      formData.append('empresa_id', selectedEmpresa);
      formData.append('password', password);

      const response = await api.post('/certificados/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setSuccess(response.data.message);
        setSelectedEmpresa('');
        setPassword('');
        setFile(null);
        document.getElementById('fileInput').value = '';
        cargarDatos();
      }
    } catch (err) {
      setError('Error subiendo certificado: ' + (err.response?.data?.error || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (empresaId, empresaNombre) => {
    if (!window.confirm(`¿Eliminar certificado de ${empresaNombre}?`)) return;

    try {
      const response = await api.delete(`/certificados/${empresaId}`);
      if (response.data.success) {
        setSuccess(response.data.message);
        cargarDatos();
      }
    } catch (err) {
      setError('Error eliminando: ' + (err.response?.data?.error || err.message));
    }
  };

  const getCertificadoEmpresa = (empresaId) => {
    return certificados.find(c => c.nombre === `empresa-${empresaId}`);
  };

  const handleTestConexion = async (empresaId, empresaNombre) => {
    setTesting(prev => ({ ...prev, [empresaId]: true }));
    setSyncResults(prev => ({ ...prev, [empresaId]: null }));
    try {
      const response = await api.get(`/dgt/test-conexion/${empresaId}`);
      setSyncResults(prev => ({
        ...prev,
        [empresaId]: {
          tipo: 'test',
          success: response.data.success,
          mensaje: response.data.success
            ? `✓ Conexión OK - ${response.data.certificado?.nombre || empresaNombre}`
            : `✗ Error: ${response.data.error}`
        }
      }));
    } catch (err) {
      setSyncResults(prev => ({
        ...prev,
        [empresaId]: {
          tipo: 'test',
          success: false,
          mensaje: `✗ Error: ${err.response?.data?.error || err.message}`
        }
      }));
    } finally {
      setTesting(prev => ({ ...prev, [empresaId]: false }));
    }
  };

  const handleSincronizar = async (empresaId, empresaNombre) => {
    if (!window.confirm(`¿Sincronizar multas desde DGT para ${empresaNombre}?\n\nEsto puede tardar varios minutos para empresas con muchas multas.`)) return;

    setSyncing(prev => ({ ...prev, [empresaId]: true }));
    setSyncResults(prev => ({
      ...prev,
      [empresaId]: { tipo: 'sync', success: true, mensaje: '◆ Conectando con DGT...' }
    }));
    try {
      const response = await api.post('/dgt/sincronizar', { empresa_id: empresaId });

      // Handle async response — poll for completion
      if (response.data.async && response.data.sync_id) {
        const syncId = response.data.sync_id;
        setSyncResults(prev => ({
          ...prev,
          [empresaId]: { tipo: 'sync', success: true, mensaje: '◆ Sincronizando con DGT...' }
        }));

        // Poll every 10 seconds
        const pollInterval = setInterval(async () => {
          try {
            const statusRes = await api.get(`/dgt/sincronizar/${syncId}`);
            const job = statusRes.data.data;

            if (job.status === 'completed') {
              clearInterval(pollInterval);
              const data = job.result;
              setSyncing(prev => ({ ...prev, [empresaId]: false }));
              setSyncResults(prev => ({
                ...prev,
                [empresaId]: {
                  tipo: 'sync',
                  success: true,
                  mensaje: '✓ Sincronización completada',
                  detalles: {
                    encontradas: data.multas_encontradas,
                    nuevas: data.multas_nuevas,
                    existentes: data.multas_existentes
                  }
                }
              }));
              if (data.multas_nuevas > 0) {
                setSuccess(`Se han importado ${data.multas_nuevas} multas nuevas de ${empresaNombre}`);
              }
            } else if (job.status === 'error') {
              clearInterval(pollInterval);
              setSyncing(prev => ({ ...prev, [empresaId]: false }));
              setSyncResults(prev => ({
                ...prev,
                [empresaId]: {
                  tipo: 'sync',
                  success: false,
                  mensaje: `✗ Error: ${job.progress || 'Error desconocido'}`
                }
              }));
            } else {
              // Still running — update progress
              setSyncResults(prev => ({
                ...prev,
                [empresaId]: { tipo: 'sync', success: true, mensaje: `◆ ${job.progress}` }
              }));
            }
          } catch (pollErr) {
            clearInterval(pollInterval);
            setSyncing(prev => ({ ...prev, [empresaId]: false }));
            setSyncResults(prev => ({
              ...prev,
              [empresaId]: {
                tipo: 'sync',
                success: false,
                mensaje: `✗ Error consultando estado: ${pollErr.message}`
              }
            }));
          }
        }, 10000);

        return; // Don't clear syncing — polling will handle it
      }

      // Legacy synchronous response (fallback)
      const data = response.data.data;
      setSyncResults(prev => ({
        ...prev,
        [empresaId]: {
          tipo: 'sync',
          success: true,
          mensaje: `✓ Sincronización completada`,
          detalles: {
            encontradas: data.multas_encontradas,
            nuevas: data.multas_nuevas,
            existentes: data.multas_existentes
          }
        }
      }));
      if (data.multas_nuevas > 0) {
        setSuccess(`Se han importado ${data.multas_nuevas} multas nuevas de ${empresaNombre}`);
      }
    } catch (err) {
      setSyncing(prev => ({ ...prev, [empresaId]: false }));
      setSyncResults(prev => ({
        ...prev,
        [empresaId]: {
          tipo: 'sync',
          success: false,
          mensaje: `✗ Error: ${err.response?.data?.error || err.message}`
        }
      }));
    }
  };

  const handleSincronizarTodas = async () => {
    if (!window.confirm('¿Sincronizar multas desde DGT para TODAS las empresas con certificado?\n\nEsto puede tardar varios minutos.')) return;

    setSyncingAll(true);
    setSyncAllResults(null);
    try {
      const response = await api.post('/dgt/sincronizar-todas');
      const data = response.data.data;
      setSyncAllResults({
        success: true,
        ...data
      });
      if (data.total_multas_nuevas > 0) {
        setSuccess(`Se han importado ${data.total_multas_nuevas} multas nuevas de ${data.empresas_procesadas} empresas`);
      }
    } catch (err) {
      setSyncAllResults({
        success: false,
        error: err.response?.data?.error || err.message
      });
    } finally {
      setSyncingAll(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/admin/dashboard')}
              className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-5 w-5" />
              <span>Volver al Panel</span>
            </button>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-gray-600">{usuario.nombre || usuario.email}</span>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-red-600 hover:text-red-700"
            >
              <LogOut className="h-5 w-5" />
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-8 w-8 text-blue-600" />
          <h1 className="text-2xl font-bold text-gray-900">Gestión de Certificados</h1>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* Formulario de subida */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Subir Certificado Digital
          </h2>
          
          <form onSubmit={handleUpload} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Empresa
                </label>
                <select
                  value={selectedEmpresa}
                  onChange={(e) => setSelectedEmpresa(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Seleccionar empresa...</option>
                  {empresas.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.nombre} {emp.cif ? `(${emp.cif})` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contraseña del certificado
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Contraseña del .p12"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Archivo certificado (.p12 / .pfx)
                </label>
                <input
                  id="fileInput"
                  type="file"
                  accept=".p12,.pfx"
                  onChange={handleFileChange}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm file:mr-4 file:py-1 file:px-3 file:rounded file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={uploading}
              className="flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {uploading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Subiendo...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Subir Certificado
                </>
              )}
            </button>
          </form>
        </div>

        {/* Lista de empresas y sus certificados */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Estado de Certificados por Empresa
            </h2>
            <button
              onClick={handleSincronizarTodas}
              disabled={syncingAll}
              className="flex items-center gap-2 px-4 py-2 text-sm bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
            >
              {syncingAll ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              {syncingAll ? 'Sincronizando...' : 'Sincronizar Todas'}
            </button>
          </div>
          {syncAllResults && (
            <div className={`mx-6 mt-4 p-3 rounded-lg text-sm ${syncAllResults.success ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-700 border border-red-200'}`}>
              {syncAllResults.success ? (
                <>
                  <p className="font-medium">Sincronización completada</p>
                  <p className="mt-1">
                    Empresas procesadas: {syncAllResults.empresas_procesadas} |
                    Multas nuevas: {syncAllResults.total_multas_nuevas} |
                    Ya existían: {syncAllResults.total_multas_existentes} |
                    Sin certificado: {syncAllResults.empresas_sin_certificado} |
                    Errores: {syncAllResults.empresas_error}
                  </p>
                  {syncAllResults.detalle?.filter(d => d.nuevas > 0).map((d, i) => (
                    <p key={i} className="mt-1 text-xs">
                      {d.nombre}: {d.nuevas} nuevas de {d.encontradas} encontradas
                    </p>
                  ))}
                </>
              ) : (
                <p>Error: {syncAllResults.error}</p>
              )}
            </div>
          )}
          
          <div className="divide-y divide-gray-200">
            {empresas.map(empresa => {
              const cert = getCertificadoEmpresa(empresa.id);
              return (
                <div key={empresa.id} className="px-6 py-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`p-2 rounded-full ${cert ? 'bg-green-100' : 'bg-gray-100'}`}>
                      <FileKey className={`h-5 w-5 ${cert ? 'text-green-600' : 'text-gray-400'}`} />
                    </div>
                    <div>
                      <p className="font-medium text-gray-900">{empresa.nombre}</p>
                      <p className="text-sm text-gray-500">{empresa.cif || 'Sin CIF'}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {cert ? (
                      <>
                        <div className="flex items-center gap-2 text-green-600 mr-2">
                          <CheckCircle className="h-5 w-5" />
                          <span className="text-sm hidden sm:inline">Activo</span>
                        </div>
                        <button
                          onClick={() => handleTestConexion(empresa.id, empresa.nombre)}
                          disabled={testing[empresa.id]}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 disabled:opacity-50"
                          title="Probar conexión con DGT"
                        >
                          {testing[empresa.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
                          <span className="hidden sm:inline">Test</span>
                        </button>
                        <button
                          onClick={() => handleSincronizar(empresa.id, empresa.nombre)}
                          disabled={syncing[empresa.id]}
                          className="flex items-center gap-1 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 disabled:opacity-50"
                          title="Sincronizar multas desde DGT"
                        >
                          {syncing[empresa.id] ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                          <span className="hidden sm:inline">Sincronizar</span>
                        </button>
                        <button
                          onClick={() => handleDelete(empresa.id, empresa.nombre)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                          title="Eliminar certificado"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </>
                    ) : (
                      <div className="flex items-center gap-2 text-gray-400">
                        <XCircle className="h-5 w-5" />
                        <span className="text-sm">Sin certificado</span>
                      </div>
                    )}
                  </div>
                  {syncResults[empresa.id] && (
                    <div className={`mt-2 p-2 rounded text-sm ${syncResults[empresa.id].success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                      <p>{syncResults[empresa.id].mensaje}</p>
                      {syncResults[empresa.id].detalles && (
                        <p className="text-xs mt-1">
                          Encontradas: {syncResults[empresa.id].detalles.encontradas} |
                          Nuevas: {syncResults[empresa.id].detalles.nuevas} |
                          Ya existían: {syncResults[empresa.id].detalles.existentes}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
